import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import AdminLayout from '../components/admin/AdminLayout';
import AdminReports from './admin/AdminReports';
import AdminFeedback from './admin/AdminFeedback';
import AdminCost from './admin/AdminCost';
import {
  getAdminStats, getAdminAgencies, toggleAgencyStatus,
  getPendingUsers, approveUser, rejectUser, getAdminAudits,
  getAgencyAuditSettings, updateAgencyAuditSettings,
  getAgencyRecommendations, downloadAdminRecommendationPdf,
  getAgencyWeaknessSummary, deleteAgency
} from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function Admin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [audits, setAudits] = useState([]);
  const [auditFilters, setAuditFilters] = useState({ agencyId: '', clientAgency: '', sortBy: 'date_desc' });
  const [loading, setLoading] = useState(true);

  // Agency settings state
  const [selectedAgencyId, setSelectedAgencyId] = useState(null);
  const [agencySettings, setAgencySettings] = useState(null);
  const [agencyRecs, setAgencyRecs] = useState([]);
  const [agencyWeaknesses, setAgencyWeaknesses] = useState([]);
  const [settingsTrigger, setSettingsTrigger] = useState(10);
  const [settingsEnabled, setSettingsEnabled] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);

  useEffect(() => {
    Promise.all([getAdminStats(), getAdminAgencies(), getPendingUsers()])
      .then(([statsRes, agenciesRes, pendingRes]) => {
        setStats(statsRes.data);
        setAgencies(agenciesRes.data.agencies || []);
        setPendingUsers(pendingRes.data.users || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const loadAudits = useCallback(() => {
    getAdminAudits(auditFilters)
      .then(res => setAudits(res.data.audits || []))
      .catch(console.error);
  }, [auditFilters]);

  useEffect(() => {
    if (location.pathname.includes('/admin/audits')) loadAudits();
  }, [location.pathname, loadAudits]);

  const handleToggle = async (agencyId, currentStatus) => {
    try {
      await toggleAgencyStatus(agencyId, !currentStatus);
      setAgencies(agencies.map(a =>
        a.id === agencyId ? { ...a, is_active: !currentStatus } : a
      ));
    } catch (err) {
      alert('Failed to update agency status');
    }
  };

  const handleApprove = async (userId) => {
    try {
      await approveUser(userId);
      setPendingUsers(pendingUsers.filter(u => u.id !== userId));
      const statsRes = await getAdminStats();
      setStats(statsRes.data);
    } catch (err) {
      alert('Failed to approve user');
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm('Are you sure you want to reject this user? They will not be able to log in.')) return;
    try {
      await rejectUser(userId);
      setPendingUsers(pendingUsers.filter(u => u.id !== userId));
    } catch (err) {
      alert('Failed to reject user');
    }
  };

  const handleDeleteAgency = async (agencyId, agencyName) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${agencyName}" and ALL associated data (users, audits, documents)? This cannot be undone.`)) return;
    if (!window.confirm(`FINAL WARNING: This will permanently delete the agency "${agencyName}", all its users, all audits, and all uploaded documents. Type OK to confirm.`)) return;
    try {
      await deleteAgency(agencyId);
      setAgencies(agencies.filter(a => a.id !== agencyId));
      const statsRes = await getAdminStats();
      setStats(statsRes.data);
      alert(`Agency "${agencyName}" has been deleted.`);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete agency');
    }
  };

  if (loading) {
    return (
      <AdminLayout pendingCount={0}>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Loading admin data...</p>
        </div>
      </AdminLayout>
    );
  }

  const chartData = stats?.recentAudits?.map(a => ({
    name: new Date(a.created_at).toLocaleDateString(),
    score: a.compliance_score || 0,
    agency: a.agency_name,
  })) || [];

  const loadAgencySettings = async (agencyId) => {
    setSelectedAgencyId(agencyId);
    try {
      const [settingsRes, recsRes, weakRes] = await Promise.all([
        getAgencyAuditSettings(agencyId),
        getAgencyRecommendations(agencyId),
        getAgencyWeaknessSummary(agencyId),
      ]);
      const s = settingsRes.data.settings;
      setAgencySettings(s);
      setSettingsTrigger(s.recommendation_trigger_count || 10);
      setSettingsEnabled(s.is_enabled !== false);
      setAgencyRecs(recsRes.data.reports || []);
      setAgencyWeaknesses(weakRes.data.weaknesses || []);
    } catch (err) {
      console.error('Failed to load agency settings:', err);
    }
  };

  const handleSaveSettings = async () => {
    if (!selectedAgencyId) return;
    setSettingsSaving(true);
    try {
      await updateAgencyAuditSettings(selectedAgencyId, {
        recommendationTriggerCount: settingsTrigger,
        isEnabled: settingsEnabled,
      });
      alert('Settings saved!');
    } catch (err) {
      alert('Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleDownloadAdminRec = async (reportId) => {
    try {
      const res = await downloadAdminRecommendationPdf(reportId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Recommendations-${reportId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Failed to download');
    }
  };

  return (
    <AdminLayout pendingCount={pendingUsers.length}>
      <Routes>
        <Route index element={
        <>
          <h2 className="text-2xl font-bold text-gray-800 mb-1">Overview</h2>
          <p className="text-gray-500 mb-6">Platform-wide summary</p>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50 p-5">
              <p className="text-sm text-gray-500">Active Agencies</p>
              <p className="text-3xl font-bold text-gray-800 mt-1">{stats?.agencies || 0}</p>
            </div>
            <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50 p-5">
              <p className="text-sm text-gray-500">Total Audits</p>
              <p className="text-3xl font-bold text-gray-800 mt-1">{stats?.audits?.total || 0}</p>
            </div>
            <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50 p-5">
              <p className="text-sm text-gray-500">Avg Score</p>
              <p className="text-3xl font-bold text-teal-600 mt-1">{Math.round(stats?.audits?.avg_score || 0)}</p>
            </div>
            <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50 p-5">
              <p className="text-sm text-gray-500">Total Critical</p>
              <p className="text-3xl font-bold text-red-600 mt-1">{stats?.audits?.total_critical || 0}</p>
            </div>
            <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50 p-5">
              <p className="text-sm text-gray-500">Pending Approvals</p>
              <p className="text-3xl font-bold text-yellow-600 mt-1">{pendingUsers.length}</p>
            </div>
          </div>

          {chartData.length > 0 && (
            <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50 p-6 mb-8">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Recent Audit Scores</h2>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Bar dataKey="score" fill="#0d9488" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
        } />
        <Route path="reports" element={<AdminReports />} />
        <Route path="feedback" element={<AdminFeedback />} />
        <Route path="costs" element={<AdminCost />} />
        <Route path="approvals" element={
        <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-800">Pending User Approvals</h2>
            <p className="text-sm text-gray-500 mt-1">New registrations that need your review</p>
          </div>
          {pendingUsers.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-400">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-3">
                <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="font-medium">No pending approvals</p>
              <p className="text-sm mt-1">All user registrations have been reviewed.</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-teal-50/50 text-left text-sm text-gray-500 font-medium">
                  <th className="px-6 py-3">Name</th>
                  <th className="px-6 py-3">Email</th>
                  <th className="px-6 py-3">Organization</th>
                  <th className="px-6 py-3">Type</th>
                  <th className="px-6 py-3">Registered</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {pendingUsers.map(user => (
                  <tr key={user.id} className="hover:bg-teal-50/30">
                    <td className="px-6 py-4 font-medium text-gray-800">
                      {user.first_name} {user.last_name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{user.email}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{user.agency_name}</td>
                    <td className="px-6 py-4">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        user.account_type === 'consultant'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-teal-100 text-teal-700'
                      }`}>
                        {user.account_type === 'consultant' ? 'Consultant' : 'Agency'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <button onClick={() => handleApprove(user.id)}
                        className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-3 py-1.5 rounded-lg transition">
                        Approve
                      </button>
                      <button onClick={() => handleReject(user.id)}
                        className="bg-red-100 hover:bg-red-200 text-red-700 text-sm font-medium px-3 py-1.5 rounded-lg transition">
                        Reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        } />
        <Route path="agencies" element={
        <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-800">All Agencies</h2>
          </div>
          <table className="w-full">
            <thead>
              <tr className="bg-teal-50/50 text-left text-sm text-gray-500 font-medium">
                <th className="px-6 py-3">Agency</th>
                <th className="px-6 py-3">Email</th>
                <th className="px-6 py-3 text-center">Users</th>
                <th className="px-6 py-3 text-center">Audits</th>
                <th className="px-6 py-3">Last Audit</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {agencies.map(agency => (
                <tr key={agency.id} className="hover:bg-teal-50/30">
                  <td className="px-6 py-4 font-medium text-gray-800">{agency.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{agency.email}</td>
                  <td className="px-6 py-4 text-center text-sm">{agency.total_users}</td>
                  <td className="px-6 py-4 text-center text-sm">{agency.total_audits}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {agency.last_audit_date ? new Date(agency.last_audit_date).toLocaleDateString() : '\u2014'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      agency.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>{agency.is_active ? 'Active' : 'Disabled'}</span>
                  </td>
                  <td className="px-6 py-4 space-x-2">
                    <button onClick={() => handleToggle(agency.id, agency.is_active)}
                      className={`text-sm font-medium px-3 py-1 rounded-lg ${
                        agency.is_active ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'
                      }`}>
                      {agency.is_active ? 'Disable' : 'Enable'}
                    </button>
                    <button onClick={() => handleDeleteAgency(agency.id, agency.name)}
                      className="text-sm font-medium px-3 py-1 rounded-lg text-red-700 bg-red-100 hover:bg-red-200 transition">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        } />
        <Route path="audits" element={
        <div>
          {/* Filters */}
          <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50 p-4 mb-4 flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Agency</label>
              <select value={auditFilters.agencyId}
                onChange={e => setAuditFilters({ ...auditFilters, agencyId: e.target.value })}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                <option value="">All Agencies</option>
                {agencies.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Client Agency</label>
              <input value={auditFilters.clientAgency}
                onChange={e => setAuditFilters({ ...auditFilters, clientAgency: e.target.value })}
                placeholder="Filter by client agency..."
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Sort</label>
              <select value={auditFilters.sortBy}
                onChange={e => setAuditFilters({ ...auditFilters, sortBy: e.target.value })}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                <option value="date_desc">Newest First</option>
                <option value="date_asc">Oldest First</option>
                <option value="score_desc">Highest Score</option>
                <option value="score_asc">Lowest Score</option>
                <option value="agency">By Agency</option>
              </select>
            </div>
            <button onClick={loadAudits}
              className="bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white text-sm font-medium px-4 py-2 rounded-xl shadow-md transition">
              Apply Filters
            </button>
          </div>

          {/* Audit Table */}
          <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-teal-50/50 text-left text-sm text-gray-500 font-medium">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Agency</th>
                  <th className="px-4 py-3">Patient</th>
                  <th className="px-4 py-3">Document</th>
                  <th className="px-4 py-3 text-center">Score</th>
                  <th className="px-4 py-3 text-center">Critical</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {audits.length === 0 ? (
                  <tr><td colSpan="9" className="px-6 py-8 text-center text-gray-400">No audits found</td></tr>
                ) : audits.map(audit => (
                  <tr key={audit.id} className="hover:bg-teal-50/30 cursor-pointer"
                    onClick={() => navigate(`/audits/${audit.id}`)}>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {new Date(audit.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        audit.service_type === 'hospice'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-teal-100 text-teal-700'
                      }`}>
                        {audit.service_type === 'hospice' ? 'Hospice' : 'HH'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <p className="font-medium text-gray-800">{audit.client_agency_name || audit.agency_name}</p>
                      {audit.client_agency_name && audit.client_agency_name !== audit.agency_name && (
                        <p className="text-xs text-gray-400">by {audit.agency_name}</p>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <p className="text-gray-700">{audit.patient_full_name || '\u2014'}</p>
                      {audit.patient_reference && (
                        <p className="text-xs text-gray-400">{audit.patient_reference}</p>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500 max-w-[180px] truncate">
                      {audit.original_filename || '\u2014'}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {audit.compliance_score != null ? (
                        <span className={`font-bold ${
                          audit.compliance_score >= 80 ? 'text-green-600' :
                          audit.compliance_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                        }`}>{audit.compliance_score}</span>
                      ) : '\u2014'}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="text-red-600 font-semibold">{audit.critical_count || 0}</span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        audit.status === 'completed' ? 'bg-green-100 text-green-700' :
                        audit.status === 'processing' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>{audit.status}</span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-teal-600 hover:text-teal-800 text-sm font-medium">View &rarr;</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        } />
        <Route path="settings" element={
        <div>
          <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Agency Recommendation Settings</h2>
            <p className="text-sm text-gray-500 mb-4">
              Configure how often ChartAudit AI generates charting improvement recommendation reports per agency.
              After the configured number of audits, a summary PDF is automatically generated with weakness analysis.
            </p>
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-500 mb-1">Select Agency</label>
              <select
                value={selectedAgencyId || ''}
                onChange={e => {
                  if (e.target.value) loadAgencySettings(e.target.value);
                  else setSelectedAgencyId(null);
                }}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent w-full max-w-md"
              >
                <option value="">Choose an agency...</option>
                {agencies.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
          </div>

          {selectedAgencyId && agencySettings && (
            <>
              {/* Settings Form */}
              <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50 p-6 mb-6">
                <h3 className="text-md font-semibold text-gray-800 mb-4">Recommendation Trigger Configuration</h3>
                <div className="flex flex-wrap gap-6 items-end">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Generate report after every N audits
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={settingsTrigger}
                      onChange={e => setSettingsTrigger(parseInt(e.target.value) || 10)}
                      className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent w-32"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Auto-generate enabled
                    </label>
                    <button
                      onClick={() => setSettingsEnabled(!settingsEnabled)}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition ${
                        settingsEnabled
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {settingsEnabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </div>
                  <button
                    onClick={handleSaveSettings}
                    disabled={settingsSaving}
                    className="bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white text-sm font-medium px-6 py-2 rounded-xl shadow-md transition disabled:opacity-50"
                  >
                    {settingsSaving ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-3">
                  Current progress: {agencySettings.audits_since_last_recommendation || 0} audits since last report
                  {agencySettings.last_recommendation_at && (
                    <> — Last report: {new Date(agencySettings.last_recommendation_at).toLocaleDateString()}</>
                  )}
                </p>
              </div>

              {/* Weakness Summary */}
              {agencyWeaknesses.length > 0 && (
                <div className="glass-card rounded-2xl shadow-sm border border-red-100/50 p-6 mb-6">
                  <h3 className="text-md font-semibold text-gray-800 mb-4">Weakness Summary for This Agency</h3>
                  <div className="space-y-2">
                    {(() => {
                      const catMap = {};
                      agencyWeaknesses.forEach(w => {
                        if (!catMap[w.category]) catMap[w.category] = { critical: 0, warning: 0 };
                        catMap[w.category][w.severity] = parseInt(w.count);
                      });
                      return Object.entries(catMap)
                        .sort(([,a],[,b]) => (b.critical*2+b.warning) - (a.critical*2+a.warning))
                        .slice(0, 8)
                        .map(([cat, data]) => (
                          <div key={cat} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                            <span className="text-sm font-medium text-gray-800">{cat}</span>
                            <div className="flex space-x-2">
                              {data.critical > 0 && <span className="bg-red-100 text-red-700 text-xs font-semibold px-2 py-0.5 rounded-full">{data.critical} critical</span>}
                              {data.warning > 0 && <span className="bg-yellow-100 text-yellow-700 text-xs font-semibold px-2 py-0.5 rounded-full">{data.warning} warnings</span>}
                            </div>
                          </div>
                        ));
                    })()}
                  </div>
                </div>
              )}

              {/* Recommendation Reports */}
              <div className="glass-card rounded-2xl shadow-sm border border-teal-100/50 p-6">
                <h3 className="text-md font-semibold text-gray-800 mb-4">Generated Recommendation Reports</h3>
                {agencyRecs.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">
                    No recommendation reports generated yet for this agency.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {agencyRecs.map(report => (
                      <div key={report.id} className="flex items-center justify-between bg-teal-50/50 rounded-xl px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-gray-800">
                            Report — {new Date(report.generated_at).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-gray-500">
                            {report.audit_count} audits analyzed — {report.total_weaknesses || 0} issues found
                          </p>
                        </div>
                        <button
                          onClick={() => handleDownloadAdminRec(report.id)}
                          className="bg-teal-600 hover:bg-teal-700 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition"
                        >
                          Download PDF
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        } />
      </Routes>
    </AdminLayout>
  );
}
